import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ReactFlow,
  Background,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { sfNodeTypes, SfCharactersProvider, SfLanguageProvider, SfReadOnlyProvider } from '../../storyflow/SfNodes';
import { mergeGraphWithMeta } from '../../workflow/workflowGraphModel';
import BleuEdge from '../../workflow/BleuEdge';

const edgeTypes = { bleu: BleuEdge };

/* ── Auto-layout: horizontal left-to-right tree ── */
function computeAutoLayout(nodes, edges) {
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const outgoing = new Map();
  const incomingMap = new Map();
  for (const e of edges) {
    if (!outgoing.has(e.source)) outgoing.set(e.source, []);
    outgoing.get(e.source).push(e);
    if (!incomingMap.has(e.target)) incomingMap.set(e.target, []);
    incomingMap.get(e.target).push(e.source);
  }

  const H = 400, V = 180;
  const startNode = nodes.find((n) => n.type === 'sfStart') || nodes[0];
  if (!startNode) return nodes;

  const col = new Map();
  const visited = new Set();
  col.set(startNode.id, 0);
  visited.add(startNode.id);
  const queue = [startNode.id];
  const deferred = [];
  let safety = nodes.length * 3;

  while ((queue.length > 0 || deferred.length > 0) && safety-- > 0) {
    let id;
    if (queue.length > 0) {
      id = queue.shift();
    } else {
      id = deferred.shift();
      if (visited.has(id)) continue;
      const parents = (incomingMap.get(id) || []).filter((p) => col.has(p));
      col.set(id, parents.length > 0 ? Math.max(...parents.map((p) => col.get(p))) + 1 : 0);
      visited.add(id);
    }
    const outs = outgoing.get(id) || [];
    const children = [...new Set(outs.map((e) => e.target))].filter((t) => nodeById.has(t));
    for (const cid of children) {
      if (visited.has(cid)) {
        const minCol = col.get(id) + 1;
        if (col.get(cid) < minCol) col.set(cid, minCol);
        continue;
      }
      const parents = (incomingMap.get(cid) || []).filter((p) => nodeById.has(p));
      if (parents.every((p) => col.has(p))) {
        col.set(cid, Math.max(...parents.map((p) => col.get(p))) + 1);
        visited.add(cid);
        queue.push(cid);
      } else {
        deferred.push(cid);
      }
    }
  }

  // Assign rows — position near average of parents
  const row = new Map();
  const sorted = [...visited].sort((a, b) => (col.get(a) || 0) - (col.get(b) || 0));
  for (const id of sorted) {
    const parents = (incomingMap.get(id) || []).filter((p) => row.has(p));
    row.set(id, parents.length > 0 ? parents.reduce((s, p) => s + row.get(p), 0) / parents.length : 0);
  }

  // Resolve vertical overlaps using node heights
  const GAP = 30;
  const byCol = new Map();
  for (const id of visited) {
    const c = col.get(id) || 0;
    if (!byCol.has(c)) byCol.set(c, []);
    byCol.get(c).push(id);
  }
  const yPos = new Map();
  for (const id of visited) yPos.set(id, row.get(id) * V);
  for (const [, ids] of byCol) {
    ids.sort((a, b) => yPos.get(a) - yPos.get(b));
    for (let i = 1; i < ids.length; i++) {
      const prevNode = nodeById.get(ids[i - 1]);
      const prevH = prevNode?.measured?.height || prevNode?.height || 120;
      const minY = yPos.get(ids[i - 1]) + prevH + GAP;
      if (yPos.get(ids[i]) < minY) yPos.set(ids[i], minY);
    }
  }

  // Unvisited
  let maxCol = Math.max(0, ...col.values()) + 1;
  const maxY = Math.max(0, ...yPos.values());
  let extraY = maxY + V + 50;
  for (const n of nodes) {
    if (!visited.has(n.id)) {
      col.set(n.id, maxCol);
      yPos.set(n.id, extraY);
      extraY += 150;
    }
  }

  return nodes.map((n) => ({
    ...n,
    position: { x: (col.get(n.id) || 0) * H + 50, y: (yPos.get(n.id) || 0) + 50 },
  }));
}

/* ══════════════════════════════════════════════════════════════
   ICONS
   ══════════════════════════════════════════════════════════════ */

const CloseIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/* ══════════════════════════════════════════════════════════════
   REWIND CONFIRMATION MODAL
   ══════════════════════════════════════════════════════════════ */

function RewindConfirmModal({ nodeName, onConfirm, onCancel }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="absolute inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={onCancel}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.92 }}
        transition={{ duration: 0.15 }}
        className="w-72 rounded-2xl border border-white/10 p-5"
        style={{ backgroundColor: '#1e1b25' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-3">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgb(251,191,36)" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span className="text-sm font-semibold text-white/90">Rewind progress</span>
        </div>
        <p className="text-xs text-white/60 mb-4 leading-relaxed">
          Current progress will be overwritten. You'll return to the point: <span className="text-white/80 font-medium">"{nodeName}"</span>
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-xl py-2 text-xs font-medium text-white/60 border border-white/10 hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 rounded-xl py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-500 transition-colors"
          >
            Confirm rewind
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ══════════════════════════════════════════════════════════════
   GRAPH VIEWER (inner, inside ReactFlowProvider)
   ══════════════════════════════════════════════════════════════ */

function StoryGraphInner({ onClose, title, graph, graphMeta, nodeHistory, characters, lang, onRewind, hideUnvisited }) {
  const visitedSet = useMemo(() => new Set(nodeHistory || []), [nodeHistory]);
  // Find the last node from history that exists in this chapter's graph
  const currentNodeId = useMemo(() => {
    if (!nodeHistory?.length || !graph?.nodes?.length) return null;
    const graphIds = new Set(graph.nodes.map((n) => n.id));
    for (let i = nodeHistory.length - 1; i >= 0; i--) {
      if (graphIds.has(nodeHistory[i])) return nodeHistory[i];
    }
    return null;
  }, [nodeHistory, graph]);

  // Merge graph with saved positions, then apply visited/hidden styling
  const { initialNodes, initialEdges } = useMemo(() => {
    if (!graph?.nodes?.length) return { initialNodes: [], initialEdges: [] };
    const merged = mergeGraphWithMeta(graph, graphMeta || { positions: {}, viewport: null });

    // Style nodes: visited = normal, unvisited = dimmed or hidden
    const styledNodes = (hideUnvisited
      ? merged.nodes.filter((n) => visitedSet.has(n.id))
      : merged.nodes
    ).map((n) => ({
      ...n,
      style: {
        ...(n.style || {}),
        opacity: visitedSet.has(n.id) ? 1 : 0.15,
        pointerEvents: visitedSet.has(n.id) ? 'all' : 'none',
        ...(n.id === currentNodeId ? { outline: '2px solid rgb(52,211,153)', outlineOffset: '2px', borderRadius: '8px' } : {}),
      },
    }));

    const visitedNodeIds = hideUnvisited ? new Set(styledNodes.map((n) => n.id)) : null;

    // Style edges: only show edges where both source and target are visited
    const styledEdges = merged.edges
      .filter((e) => !hideUnvisited || (visitedNodeIds.has(e.source) && visitedNodeIds.has(e.target)))
      .map((e) => ({
        ...e,
        style: {
          ...(e.style || {}),
          opacity: visitedSet.has(e.source) && visitedSet.has(e.target) ? 1 : 0.08,
        },
        animated: visitedSet.has(e.source) && visitedSet.has(e.target),
      }));

    return { initialNodes: styledNodes, initialEdges: styledEdges };
  }, [graph, graphMeta, visitedSet, currentNodeId]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);
  const { setCenter } = useReactFlow();
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [showRewindConfirm, setShowRewindConfirm] = useState(false);
  const [loading, setLoading] = useState(true);

  // Update nodes/edges when graph changes
  useEffect(() => { setNodes(initialNodes); setLoading(true); }, [initialNodes, setNodes]);

  // Focus on current node after render, then reveal
  useEffect(() => {
    if (!loading || nodes.length === 0) return;
    const t = setTimeout(() => {
      const target = currentNodeId ? nodes.find((n) => n.id === currentNodeId) : null;
      if (target) {
        const w = target.measured?.width || 320;
        const h = target.measured?.height || 200;
        setCenter(
          (target.position?.x ?? 0) + w / 2,
          (target.position?.y ?? 0) + h / 2,
          { zoom: 0.9, duration: 0 }
        );
      }
      setLoading(false);
    }, 500);
    return () => clearTimeout(t);
  }, [loading, nodes, currentNodeId, setCenter]);

  // Block all node changes except selection
  const handleNodesChange = useCallback((changes) => {
    const selectChanges = changes.filter((c) => c.type === 'select');
    if (selectChanges.length > 0) {
      onNodesChange(selectChanges);
      const selected = selectChanges.find((c) => c.selected);
      setSelectedNodeId(selected ? selected.id : null);
    }
  }, [onNodesChange]);

  const handleNodeClick = useCallback((_, node) => {
    if (visitedSet.has(node.id)) setSelectedNodeId(node.id);
  }, [visitedSet]);

  const handlePaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId),
    [nodes, selectedNodeId]
  );

  // Can rewind: only sfDialogue nodes that are in history (not the current one)
  const canRewind = useMemo(() => {
    if (!selectedNode || !onRewind) return false;
    if (selectedNode.id === currentNodeId) return false;
    if (selectedNode.type !== 'sfDialogue') return false;
    return visitedSet.has(selectedNode.id);
  }, [selectedNode, currentNodeId, visitedSet, onRewind]);

  const selectedNodeName = useMemo(() => {
    if (!selectedNode) return '';
    const d = selectedNode.data;
    if (selectedNode.type === 'sfStart') return 'Start';
    if (d.speakerName && d.text) return `${d.speakerName}: "${d.text.slice(0, 30)}${d.text.length > 30 ? '...' : ''}"`;
    if (d.speakerName) return d.speakerName;
    if (d.label) return d.label;
    return 'Node';
  }, [selectedNode]);

  const handleRewindClick = useCallback(() => {
    setShowRewindConfirm(true);
  }, []);

  const handleRewindConfirm = useCallback(() => {
    setShowRewindConfirm(false);
    if (selectedNodeId && onRewind) {
      onRewind(selectedNodeId);
    }
    setSelectedNodeId(null);
  }, [selectedNodeId, onRewind]);

  const charList = useMemo(() => (characters || []).map((c) => ({
    id: c.characterId || c.id,
    name: c.name,
    avatar: c.avatar ? { url64: c.avatar.url64 || c.avatar.r2Key64 } : null,
  })), [characters]);

  return (
    <div className="flex h-full flex-col pt-6" style={{ backgroundColor: '#09090b' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 shrink-0 border-b border-white/10" style={{ backgroundColor: '#141218' }}>
        <span className="text-xs font-medium text-white/60 truncate">{title || 'Story Graph'}</span>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg text-white/50 hover:text-white/80 hover:bg-white/5 transition-colors"
        >
          {CloseIcon}
        </button>
      </div>

      {/* Graph area */}
      <div className="flex-1 relative" style={{ minHeight: 0 }}>
        <SfReadOnlyProvider value={true}>
          <SfCharactersProvider value={charList}>
            <SfLanguageProvider value={lang || 'en'}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={sfNodeTypes}
                edgeTypes={edgeTypes}
                onNodesChange={handleNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={handleNodeClick}
                onPaneClick={handlePaneClick}
                nodesConnectable={false}
                nodesDraggable={false}
                elementsSelectable={true}
                selectionOnDrag={false}
                panOnDrag={true}
                multiSelectionKeyCode={null}
                selectionKeyCode={null}
                deleteKeyCode={null}
                minZoom={0.05}
                maxZoom={2}
                fitView={false}
                proOptions={{ hideAttribution: true }}
              >
                <Background color="rgba(255,255,255,0.06)" gap={20} size={1} />
              </ReactFlow>
            </SfLanguageProvider>
          </SfCharactersProvider>
        </SfReadOnlyProvider>

        {/* Loading overlay — hides graph while layout computes */}
        <AnimatePresence>
          {loading && (
            <motion.div
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 z-30 flex items-center justify-center"
              style={{ backgroundColor: '#09090b' }}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="h-5 w-5 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                <span className="text-xs text-white/40">Loading graph...</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Rewind button — only for visited sfDialogue nodes (not current) */}
        <AnimatePresence>
          {canRewind && !showRewindConfirm && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.18 }}
              className="absolute bottom-16 left-0 right-0 flex justify-center z-40"
            >
              <button
                type="button"
                onClick={handleRewindClick}
                className="flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-colors"
                style={{ backgroundColor: 'rgb(22,163,74)', boxShadow: '0 4px 20px rgba(22,163,74,0.35)' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 105.64-11.36L1 10" />
                </svg>
                Rewind here
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Rewind confirmation modal */}
        <AnimatePresence>
          {showRewindConfirm && (
            <RewindConfirmModal
              nodeName={selectedNodeName}
              onConfirm={handleRewindConfirm}
              onCancel={() => setShowRewindConfirm(false)}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   STORY GRAPH VIEWER — public API
   ══════════════════════════════════════════════════════════════ */

export default function StoryGraphViewer({ open, onClose, title, graph, graphMeta, nodeHistory, characters, lang, onRewind, hideUnvisited }) {
  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="absolute inset-0 z-40"
      style={{ backgroundColor: '#09090b' }}
    >
      <ReactFlowProvider>
        <StoryGraphInner
          onClose={onClose}
          title={title}
          graph={graph}
          graphMeta={graphMeta}
          nodeHistory={nodeHistory}
          characters={characters}
          lang={lang}
          onRewind={onRewind}
          hideUnvisited={hideUnvisited}
        />
      </ReactFlowProvider>
    </motion.div>
  );
}
